import type { ReactNode, RefObject } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import Button from './Button';
import { cn } from '../../lib/cn';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Panel width. Default 'md' (max-w-lg), matching the previous Modal. */
  size?: ModalSize;
  /** Set false for a blocking review dialog (no Escape, no scrim-click close). Default true. */
  dismissable?: boolean;
  /** Element to focus when the dialog opens (e.g. a confirm button). */
  initialFocus?: RefObject<HTMLElement | null>;
  footer?: ReactNode;
  children: ReactNode;
}

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[min(1200px,92vw)]',
};

export default function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  dismissable = true,
  initialFocus,
  footer,
  children,
}: ModalProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        {/* Scrollable-overlay pattern: the scrim owns centering + overflow. */}
        <Dialog.Overlay className="fixed inset-0 z-(--z-modal) grid place-items-center overflow-y-auto bg-ink/40 p-4 animate-scrim">
          <Dialog.Content
            className={cn(
              'relative flex max-h-[90vh] min-h-0 w-full flex-col',
              'rounded-overlay border border-border bg-surface-card shadow-modal animate-dialog',
              SIZE[size],
            )}
            onOpenAutoFocus={
              initialFocus
                ? (e) => {
                    e.preventDefault();
                    initialFocus.current?.focus();
                  }
                : undefined
            }
            onEscapeKeyDown={dismissable ? undefined : (e) => e.preventDefault()}
            onPointerDownOutside={dismissable ? undefined : (e) => e.preventDefault()}
            onInteractOutside={dismissable ? undefined : (e) => e.preventDefault()}
            {...(description ? {} : { 'aria-describedby': undefined })}
          >
            {/* Header is pinned — never inside the scroll container. */}
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
              <div className="min-w-0">
                <Dialog.Title className="t-card text-green-900">{title}</Dialog.Title>
                {description && (
                  <Dialog.Description className="t-caption mt-1">{description}</Dialog.Description>
                )}
              </div>
              {dismissable && (
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="-m-1 shrink-0 rounded-control p-1 text-ink-faint transition-colors hover:text-ink"
                  >
                    <X size={20} />
                  </button>
                </Dialog.Close>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {footer && (
              <div className="flex shrink-0 justify-end gap-2 border-t border-border-subtle px-5 py-4">
                {footer}
              </div>
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Lead with the consequence, not "Are you sure you want to…". */
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
  loading?: boolean;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  loading = false,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-body text-ink-soft">{message}</p>
    </Modal>
  );
}
